import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Send } from 'lucide-react'
import Modal from './Modal'
import { api } from '../lib/api'

interface ResumeApplicationModalProps {
  isOpen: boolean
  onClose: () => void
  vacancyId: string
  vacancyTitle: string
}

const MAX_RESUME_SIZE = 5 * 1024 * 1024
const ALLOWED_RESUME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

const initialFormData = {
  name: '',
  email: '',
  phone: '',
  message: '',
  website: '',
}

const ResumeApplicationModal = ({
  isOpen,
  onClose,
  vacancyId,
  vacancyTitle,
}: ResumeApplicationModalProps) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState(initialFormData)
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    setFormData(initialFormData)
    setResumeFile(null)
    setAcceptedPrivacyPolicy(false)
    setIsSubmitting(false)
    setIsSubmitted(false)
    setSubmitError(null)
  }, [isOpen, vacancyId])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      setResumeFile(null)
      return
    }

    if (!ALLOWED_RESUME_TYPES.includes(file.type)) {
      setSubmitError(t('vacancys.applyForm.resumeInvalidType'))
      setResumeFile(null)
      e.target.value = ''
      return
    }

    if (file.size > MAX_RESUME_SIZE) {
      setSubmitError(t('vacancys.applyForm.resumeTooLarge'))
      setResumeFile(null)
      e.target.value = ''
      return
    }

    setSubmitError(null)
    setResumeFile(file)
  }

  const fileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        resolve(result.split(',')[1] || '')
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!acceptedPrivacyPolicy) {
      setSubmitError(t('vacancys.applyForm.consentRequired'))
      return
    }

    if (!resumeFile) {
      setSubmitError(t('vacancys.applyForm.resumeRequired'))
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const resumeBase64 = await fileToBase64(resumeFile)

      await api.vacancyApplications.submit({
        ...formData,
        vacancyId,
        vacancyTitle,
        resumeFileName: resumeFile.name,
        resumeMimeType: resumeFile.type,
        resumeBase64,
        consent: acceptedPrivacyPolicy,
      })

      setIsSubmitted(true)
    } catch (err) {
      const message = err instanceof Error ? err.message : t('vacancys.applyForm.submitError')
      setSubmitError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('vacancys.applyForm.title')}
    >
      {isSubmitted ? (
        <div className="text-center py-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('vacancys.applyForm.successTitle')}
          </h3>
          <p className="text-gray-600 mb-6">{t('vacancys.applyForm.successMessage')}</p>
          <button type="button" onClick={onClose} className="btn-primary">
            {t('vacancys.applyForm.close')}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-lg bg-primary-50 px-3 py-2 text-sm text-primary-800">
            <span className="font-medium">{t('vacancys.applyForm.vacancy')}:</span> {vacancyTitle}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('vacancys.applyForm.name')}
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('vacancys.applyForm.namePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('vacancys.applyForm.email')}
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder={t('vacancys.applyForm.emailPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('vacancys.applyForm.phone')}
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder={t('vacancys.applyForm.phonePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('vacancys.applyForm.resume')}
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFileChange}
              className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              required
            />
            <p className="mt-1 text-xs text-gray-500">{t('vacancys.applyForm.resumeHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('vacancys.applyForm.message')}
            </label>
            <textarea
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder={t('vacancys.applyForm.messagePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <input
            type="text"
            name="website"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            className="hidden"
            tabIndex={-1}
            autoComplete="off"
          />

          {submitError && <p className="text-sm text-red-600">{submitError}</p>}

          <label className="flex items-start gap-3 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={acceptedPrivacyPolicy}
              onChange={(e) => setAcceptedPrivacyPolicy(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>
              {t('vacancys.applyForm.consent')}{' '}
              <Link
                to="/privacy-policy"
                className="text-primary-700 hover:text-primary-800 underline font-medium"
              >
                {t('vacancys.applyForm.privacyPolicy')}
              </Link>
            </span>
          </label>

          <button
            type="submit"
            disabled={!acceptedPrivacyPolicy || isSubmitting}
            className="w-full btn-primary flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t('vacancys.applyForm.submitting') : t('vacancys.applyForm.submit')}
            <Send className="w-4 h-4 ml-2" />
          </button>
        </form>
      )}
    </Modal>
  )
}

export default ResumeApplicationModal
